/**
 * Site prep.
 *
 * **Sourced, and the mapping is the most solid in the set.** The real form is
 * `Site Prep Supplier Form` (`SV_4O6UTfG2XJ0ZfZY`, 786 responses, 19 questions of which 10 are
 * photo uploads). Unusually, its Qualtrics→Salesforce workflow *was* captured, so every field
 * below except the two noted ones is mapped to a `Work_Submission__c` field that was observed
 * being written, under record type `012at0000005Ns1AAE`, in an enabled workflow. That is a
 * stronger claim than name-matching a describe.
 *
 * A note on the earlier reading of this form. Discovery originally paired "site prep" with
 * `Locker_Consultation_Surveys_-_EN.qsf`, which is an untouched Qualtrics blank template — one
 * placeholder question. That pairing was by filename. The real form is this one.
 *
 * The character of the form matters for the UI: 10 of 19 questions are photos, and the concrete
 * dimensions exist to be *proved*, not merely stated. So each dimension is immediately followed by
 * the photo that evidences it, with the tape measure in frame. A number without its photo is not
 * evidence, and discovering that after the crew has left means someone drives back out.
 *
 * Two questions in the source are deliberately not asked:
 *  - `Q26` "Please provide Case/PI ID" — the form already receives `caseID` as a parameter and
 *    then asks the technician to retype it. Prefilled.
 *  - `Q28` "Location Name" — asked in the source and **mapped to nothing**, so it is collected and
 *    discarded. Prefilled from the record, and flagged as unmapped.
 *
 * **`Q26` is carried on two questions — `case_id` and `permit_doc` — and both are correct.** The
 * source form reuses the `Q26` tag for "Please provide Case/PI ID" and for the permit-inspection
 * upload. The two are unrelated questions sharing one export column upstream, which is a defect in
 * `Site Prep Supplier Form`, not here: the field ids and the Salesforce targets are distinct
 * (`Case__c` and `Permit_inspection_documentation__c`), so no answer is lost. The tag is kept on
 * both because it is a pointer into the source artefact, and a tag that points at nothing is worse
 * than a tag that points at an ambiguity.
 */

import type { FormSchema } from '../types.ts';

const WS = 'Work_Submission__c';
const WORKFLOW =
  'discovery/qualtrics/10-WORKFLOWS-AND-SF-MAPPING.md — Site Prep Supplier Form, workflow WF_5ZFFLmlzn7Hw3DU, task OCAC_7udQ8pAYGOXUAY2, Insert Work_Submission__c record type 012at0000005Ns1AAE, 20 fields, ENABLED';

/** Observed in the live workflow mapping. The strongest confidence available. */
function mapped(field: string, type: string, caveat?: string) {
  return {
    object: WS,
    field,
    type,
    confidence: 'verified' as const,
    evidence: WORKFLOW,
    ...(caveat !== undefined ? { caveat } : {}),
  };
}

const CHECKLIST = [
  'Electrical Installation Outlet',
  'Electrical Installation Disconnect',
  'Concrete Base',
  'Access Improvement',
  'Installation of bollards/curb stops',
] as const;

const DOING_CONCRETE = { field: 'checklist', op: 'includes' as const, value: ['Concrete Base'] };
const DOING_ELECTRICAL = {
  field: 'checklist',
  op: 'includes' as const,
  value: ['Electrical Installation Outlet', 'Electrical Installation Disconnect'],
};
const DOING_ACCESS = {
  field: 'checklist',
  op: 'includes' as const,
  value: ['Access Improvement', 'Installation of bollards/curb stops'],
};

export const SITE_PREP: FormSchema = {
  id: 'site-prep',
  version: 1,
  title: 'Site prep',
  source: {
    provenance: 'sourced',
    artefact: 'Site Prep Supplier Form — Qualtrics SV_4O6UTfG2XJ0ZfZY (786 responses, 19 questions, 10 photo uploads)',
    sourceQuestionCount: 19,
    note:
      'Question text and required-ness from discovery/qualtrics/Site_Prep_Supplier_Form.md; field mapping from the captured enabled workflow. The source has no display logic at all — every question is asked on every visit regardless of what work was actually scoped. The checklist gating below is the one structural addition, and it is flagged inferred: the checklist question exists in the source (Q23) but does not gate anything there.',
  },
  destination: {
    object: 'Work_Submission__c',
    recordTypeId: '012at0000005Ns1AAE',
    recordTypeLabel: 'Site Prep Supplier Form',
    confidence: 'verified',
    evidence: WORKFLOW,
  },
  sections: [
    {
      id: 'visit',
      title: 'This visit',
      fields: [
        {
          id: 'kiosk_id',
          kind: 'text',
          label: 'Kiosk ID',
          prefill: { source: 'kiosk.id' },
          maps: mapped('Kiosk_ID__c', 'string(255)', 'Not in the site-prep workflow\'s 20 fields — the source form identifies the visit by case, not by kiosk. Included because the field exists and the PWA always knows the kiosk.'),
        },
        {
          id: 'case_id',
          kind: 'text',
          label: 'Case / PI ID',
          help: 'The source form receives this as a parameter and asks for it anyway. Shown, not asked.',
          required: true,
          prefill: { source: 'case.number' },
          sourceTag: 'Q26',
          maps: mapped('Case__c', 'reference to Case', 'Fed from the caseID URL parameter in the live workflow. Refused by the PWA write allowlist (UNCORRECTED_LINK_FIELDS in infra/src/writer/field-policy.ts), so it is carried as context.'),
        },
        {
          id: 'location_name',
          kind: 'text',
          label: 'Location name',
          help: 'Asked in the source form and mapped to nothing — collected and thrown away. Prefilled here instead.',
          prefill: { source: 'kiosk.siteName' },
          sourceTag: 'Q28',
          // No `maps`: deliberately unmapped, matching the source. The lint warning is correct.
        },
        {
          id: 'country_code',
          kind: 'text',
          label: 'Country',
          prefill: { source: 'kiosk.countryCode' },
          maps: mapped('Country_code__c', 'picklist', 'Fed from the countrycode parameter in the live workflow. Refused by the write allowlist: PLATFORM_CORRECTED_FIELDS, after UAT proved the platform rewrites GB and DE to FR.'),
        },
        {
          id: 'checklist',
          kind: 'multi',
          label: 'Select the site prep supplier checklist',
          options: [...CHECKLIST],
          required: true,
          prefill: { source: 'sitePrep.supplierChecklist', overrideHint: 'Correct it if the scope changed on the day.' },
          validate: { minSelections: 1 },
          sourceTag: 'Q23',
          help: 'What you were scoped to do. Everything below follows from this, which is the one thing this form should have been doing all along.',
          maps: mapped('Site_Prep_Supplier_Checklist__c', 'multipicklist'),
        },
        {
          id: 'checklist_note',
          kind: 'note',
          label:
            'In the source form every question is asked on every visit, so a crew that only poured concrete still gets asked for a breaker-board photo. Gating on this answer is the single change that makes the form match the job.',
        },
      ],
    },
    {
      id: 'concrete',
      title: 'Concrete base',
      subtitle:
        'Measure, then photograph the measurement with the tape still in place. If the tape is not readable in the frame the evidence does not count.',
      showIf: DOING_CONCRETE,
      fields: [
        {
          id: 'base_length',
          kind: 'number',
          label: 'Concrete base length (cm)',
          required: true,
          validate: { min: 1, max: 5000 },
          sourceTag: 'Q1',
          maps: mapped('Concrete_Base_Length_In_cm__c', 'double'),
        },
        {
          id: 'base_length_photo',
          kind: 'photo',
          label: 'Picture with a ruler showing the length',
          required: true,
          validate: { minPhotos: 1, maxPhotos: 3 },
          sourceTag: 'Q4',
          maps: mapped('Pic_Link_Len_Of_Excavation_ConcreteBase__c', 'url'),
        },
        {
          id: 'base_width',
          kind: 'number',
          label: 'Concrete base width (cm)',
          required: true,
          validate: { min: 1, max: 5000 },
          sourceTag: 'Q3',
          maps: mapped('Concrete_Base_Width_In_cm__c', 'double'),
        },
        {
          id: 'base_width_photo',
          kind: 'photo',
          label: 'Picture with a ruler showing the width',
          required: true,
          validate: { minPhotos: 1, maxPhotos: 3 },
          sourceTag: 'Q6',
          maps: mapped('Pic_Link_Wid_Of_Excavation_ConcreteBase__c', 'url'),
        },
        {
          id: 'base_depth',
          kind: 'number',
          label: 'Concrete base depth (cm)',
          required: true,
          validate: { min: 1, max: 500 },
          sourceTag: 'Q2',
          maps: mapped('Concrete_Base_Depth_In_cm__c', 'double'),
        },
        {
          id: 'base_depth_photo',
          kind: 'photo',
          label: 'Picture with a ruler showing the depth',
          required: true,
          validate: { minPhotos: 1, maxPhotos: 3 },
          sourceTag: 'Q5',
          maps: mapped('Pic_Link_Dep_Of_Excavation_ConcreteBase__c', 'url'),
        },
        {
          id: 'mesh_laid',
          kind: 'single',
          label: 'Was welded mesh positioned before the pour?',
          options: ['Yes', 'No', 'The base was already poured before this visit'],
          required: true,
          inferred: true,
          help:
            'The source asks only for the mesh photo, which cannot be supplied once the concrete is down. Asking first means a crew arriving after the pour is not stuck on an impossible required photo.',
          maps: mapped('Pic_of_welded_mesh__c', 'url', 'Mapping the answer to the photo field is wrong — this is a distinct fact with no field of its own. Recorded so the gap is visible rather than silent.'),
        },
        {
          id: 'mesh_photo',
          kind: 'photo',
          label: 'Picture of welded mesh positioned and ready for concrete pouring',
          required: true,
          showIf: { field: 'mesh_laid', op: 'eq', value: 'Yes' },
          validate: { minPhotos: 1, maxPhotos: 2 },
          sourceTag: 'Q7',
          maps: mapped(
            'Pic_of_welded_mesh__c',
            'url',
            'The real owner of this field, and it shares it with mesh_laid — which is visible at the same time, since this photo is shown precisely when mesh_laid = Yes. So on a mesh-laid visit two visible fields target one url field and one of them loses. This half is the correct one; the collision exists because mesh_laid has no field of its own (see its caveat). Fixing it needs a new Salesforce field for the yes/no answer, not a remap, so it is declared rather than papered over — see DECLARED_COLLISIONS in test/forms/schema-defects.test.ts.',
          ),
        },
        {
          id: 'mesh_absent_why',
          kind: 'textarea',
          label: 'Why was no mesh laid?',
          required: true,
          showIf: { field: 'mesh_laid', op: 'eq', value: 'No' },
          inferred: true,
          validate: { maxLength: 3000 },
          maps: mapped('Additional_Comments__c', 'textarea(3000)', 'Not in the site-prep workflow\'s 20 fields. Describe says 3000 characters; infra declares 32768.'),
        },
        {
          id: 'base_flat_photo',
          kind: 'photo',
          label: 'Picture proving flatness of the concrete',
          help: 'A level on the finished surface, readable in frame.',
          required: true,
          sourceTag: 'Q17',
          maps: mapped('Pic_Link_1_Of_Concrete_Base_Flatness__c', 'url'),
        },
      ],
    },
    {
      id: 'electrical',
      title: 'Electrical work',
      showIf: DOING_ELECTRICAL,
      fields: [
        {
          id: 'cord_path_photo',
          kind: 'photo',
          label: 'Picture of the electrical cord path',
          help: 'The whole run, from the board to the locker position.',
          required: true,
          validate: { minPhotos: 1, maxPhotos: 4 },
          sourceTag: 'Q14',
          maps: mapped('Picture_Link_Of_Electrical_Cord_Path__c', 'url'),
        },
        {
          id: 'breaker_photo',
          kind: 'photo',
          label: 'Picture of the breaker / distribution board',
          help: 'With the locker breaker labelled.',
          required: true,
          sourceTag: 'Q20',
          maps: mapped('Pic_of_breaker_board__c', 'url'),
        },
      ],
    },
    {
      id: 'access',
      title: 'Access improvement',
      showIf: DOING_ACCESS,
      fields: [
        {
          id: 'path_photo',
          kind: 'photo',
          label: 'Picture of the path to the locker',
          required: true,
          validate: { minPhotos: 1, maxPhotos: 3 },
          sourceTag: 'Q10',
          maps: mapped('Pic_of_pathway_clearance_to_locker__c', 'url'),
        },
      ],
    },
    {
      id: 'permit',
      title: 'Permit and paperwork',
      fields: [
        {
          id: 'permit_doc',
          kind: 'photo',
          label: 'Permit inspection documentation',
          validate: { maxPhotos: 5 },
          sourceTag: 'Q26',
          maps: mapped('Permit_inspection_documentation__c', 'url'),
        },
        {
          id: 'additional_photo',
          kind: 'photo',
          label: 'Additional picture',
          validate: { maxPhotos: 10 },
          sourceTag: 'Q21',
          maps: mapped('Additional_pictures__c', 'url'),
        },
        {
          id: 'ready_to_install',
          kind: 'yesno',
          label: 'Is this site ready to install?',
          required: true,
          inferred: true,
          help:
            'Not in the source form, which records the work done but never states the conclusion. Whether the next crew can be dispatched is the one thing anyone downstream actually wants to know.',
          maps: mapped('Installation_form_submission_status__c', 'picklist Yes|No', 'Poor fit; the object has no site-readiness field. Sim_Ticket__c.PI_SPV_status__c is the closest real concept and lives on a different object.'),
        },
        {
          id: 'blocking',
          kind: 'textarea',
          label: 'What is blocking the install?',
          required: true,
          showIf: { field: 'ready_to_install', op: 'eq', value: false },
          inferred: true,
          validate: { maxLength: 3000 },
          maps: mapped('Additional_Comments_if_needed__c', 'textarea(20000)'),
        },
      ],
    },
  ],
};
